// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Calendar,
  Clock,
  FileText,
  User,
  Phone,
  Mail,
  Landmark,
  Hash,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MobileEmptyState } from '../ui/MobileEmptyState';

const statusTone = (status: string) => {
  const s = String(status || '').toLowerCase();
  if (s.includes('conclu') || s.includes('finaliz')) return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  if (s.includes('pend')) return 'bg-amber-500/15 text-amber-700 dark:text-amber-300';
  if (s.includes('cancel') || s.includes('arquiv')) return 'bg-rose-500/15 text-rose-700 dark:text-rose-300';
  return 'bg-sky-500/15 text-sky-700 dark:text-sky-300';
};

const priorityTone = (priority: string) => {
  const p = String(priority || '').toLowerCase();
  if (p.includes('alta') || p.includes('crít')) return 'bg-rose-500/15 text-rose-700 dark:text-rose-300';
  if (p.includes('méd') || p.includes('media')) return 'bg-amber-500/15 text-amber-700 dark:text-amber-300';
  if (p.includes('baix')) return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  return 'bg-muted text-muted-foreground';
};

const formatDate = (v?: string) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
};

const MobileAdministrativeProcessDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState<any>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('administrative_processes')
          .select('*, clients:client_id(id, name, email, phone)')
          .eq('id', id)
          .single();

        if (error) throw error;
        if (!cancelled) setDetails(data);
      } catch (err: any) {
        if (!cancelled) {
          toast({
            title: 'Erro ao carregar processo administrativo',
            description: err?.message || 'Não foi possível abrir o processo.',
            variant: 'destructive',
          });
          navigate('/pwa/cases');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id, navigate, toast]);

  const subtitle = useMemo(() => {
    if (!details) return '';
    const parts = [details.process_number, details.administrative_agency].filter(Boolean);
    return parts.join(' · ');
  }, [details]);

  if (loading) {
    return (
      <div className="px-4 py-10 text-center text-sm text-muted-foreground">
        Carregando processo administrativo...
      </div>
    );
  }

  if (!details) {
    return (
      <MobileEmptyState
        className="mt-8"
        icon={<Building2 className="h-6 w-6" />}
        title="Processo administrativo não encontrado"
        description="Este registro pode ter sido removido ou você não possui acesso."
        action={
          <Button onClick={() => navigate('/pwa/cases')}>
            Voltar para Processos
          </Button>
        }
      />
    );
  }

  return (
    <div className="pb-6">
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center gap-2 px-2 py-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 shrink-0"
            onClick={() => navigate('/pwa/cases')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <span className="text-sm font-semibold truncate flex-1">
            Processo Administrativo
          </span>
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="rounded-2xl bg-card border border-border p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0 bg-sky-500/15 text-sky-600 dark:text-sky-400">
              <Building2 className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                Administrativo
              </p>
              <h1 className="font-bold text-lg leading-tight mt-0.5">
                {details.title || 'Sem título'}
              </h1>
              {subtitle && (
                <p className="text-xs text-muted-foreground mt-1 truncate">{subtitle}</p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 mt-1">
            {details.status && (
              <Badge className={`${statusTone(details.status)} border-0 text-[10px]`}>
                {details.status}
              </Badge>
            )}
            {details.priority && (
              <Badge className={`${priorityTone(details.priority)} border-0 text-[10px]`}>
                {details.priority}
              </Badge>
            )}
            {details.procedure_type && (
              <Badge className="bg-primary/15 text-primary border-0 text-[10px]">
                {details.procedure_type}
              </Badge>
            )}
          </div>
        </div>

        <div className="rounded-2xl bg-card border border-border divide-y divide-border">
          <div className="px-4 py-3 flex items-center justify-between text-sm">
            <span className="text-muted-foreground inline-flex items-center gap-1.5">
              <Hash className="h-3.5 w-3.5" />
              Número
            </span>
            <span className="font-medium text-right ml-3 truncate">{details.process_number || '—'}</span>
          </div>
          <div className="px-4 py-3 flex items-center justify-between text-sm">
            <span className="text-muted-foreground inline-flex items-center gap-1.5">
              <Landmark className="h-3.5 w-3.5" />
              Órgão
            </span>
            <span className="font-medium text-right ml-3 truncate">{details.administrative_agency || '—'}</span>
          </div>
          <div className="px-4 py-3 flex items-center justify-between text-sm">
            <span className="text-muted-foreground inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              Prazo
            </span>
            <span className="font-medium">{formatDate(details.deadline)}</span>
          </div>
          <div className="px-4 py-3 flex items-center justify-between text-sm">
            <span className="text-muted-foreground inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              Criado em
            </span>
            <span className="font-medium">{formatDate(details.created_at)}</span>
          </div>
        </div>

        {details.description && (
          <div className="rounded-2xl bg-card border border-border p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2 inline-flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              Descrição
            </p>
            <p className="text-sm whitespace-pre-wrap leading-relaxed">
              {details.description}
            </p>
          </div>
        )}

        {details.clients && (
          <div className="rounded-2xl bg-card border border-border overflow-hidden">
            <div className="px-4 pt-3 pb-1">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                Cliente
              </p>
            </div>
            <div className="px-4 py-3 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
                <User className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[15px] truncate">{details.clients.name}</p>
                {details.clients.email && (
                  <p className="text-xs text-muted-foreground truncate">{details.clients.email}</p>
                )}
              </div>
            </div>
            <div className="border-t border-border grid grid-cols-2 divide-x divide-border">
              <a
                href={details.clients.phone ? `tel:${details.clients.phone}` : '#'}
                aria-disabled={!details.clients.phone}
                className={`flex items-center justify-center gap-1.5 py-3 text-xs font-medium active:bg-muted ${!details.clients.phone ? 'opacity-40 pointer-events-none' : ''}`}
              >
                <Phone className="h-4 w-4 text-emerald-500" />
                Ligar
              </a>
              <a
                href={details.clients.email ? `mailto:${details.clients.email}` : '#'}
                aria-disabled={!details.clients.email}
                className={`flex items-center justify-center gap-1.5 py-3 text-xs font-medium active:bg-muted ${!details.clients.email ? 'opacity-40 pointer-events-none' : ''}`}
              >
                <Mail className="h-4 w-4 text-sky-500" />
                E-mail
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileAdministrativeProcessDetail;

